"use client";

import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
  DrawerHeader,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import CookieSettings from "./cookie-setting";
import Container from "./ui/container";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCookieConsent } from "@/hooks/use-cookie-consent";

export default function CookieBanner() {
  const [showSettings, setShowSettings] = useState(false);
  const isMobile = useIsMobile();
  const { loading, hasConsent, acceptAll, rejectAll } = useCookieConsent();

  if (loading || hasConsent) return null;

  const content = (
    <Alert className='border-none p-0'>
      <AlertTitle className='mb-4 mt-4 text-2xl'>Cookie Preferences</AlertTitle>
      <AlertDescription>
        <div className='space-y-4'>
          <p>
            We use cookies to enhance your browsing experience, serve
            personalized content, and analyze our traffic. These cookies help us
            understand how you interact with our website and allow us to improve
            your experience.
          </p>
          <div className='flex flex-col sm:flex-row gap-3'>
            <Button
              variant='outline'
              className='flex-1'
              onClick={() => setShowSettings(true)}
            >
              Customize Preferences
            </Button>
            <Button variant='outline' className='flex-1' onClick={rejectAll}>
              Reject All
            </Button>
            <Button className='flex-1' onClick={acceptAll}>
              Accept All
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );

  return (
    <>
      {isMobile ? (
        <>
          <Drawer open={showSettings} onOpenChange={setShowSettings}>
            <DrawerContent className='p-0 z-[102]'>
              <DrawerHeader>
                <DrawerTitle>Cookie Settings</DrawerTitle>
                <DrawerDescription>
                  Customize your cookie preferences
                </DrawerDescription>
              </DrawerHeader>
              <div className='p-4 h-[90dvh]'>
                <ScrollArea className='h-full'>
                  <CookieSettings
                    showTitle={true}
                    onSave={() => setShowSettings(false)}
                  />
                </ScrollArea>
              </div>
            </DrawerContent>
          </Drawer>
          <Drawer
            open={!hasConsent}
            onOpenChange={async (open) => {
              if (!open) {
                // When drawer is dismissed, accept all cookies
                await acceptAll();
              }
            }}
          >
            <DrawerContent className='px-8 pb-8 pt-2 z-[101]'>
              <DrawerHeader className='px-0'>
                <DrawerTitle className='sr-only'>Cookie Consent</DrawerTitle>
                <DrawerDescription className='sr-only'>
                  Manage your cookie preferences
                </DrawerDescription>
              </DrawerHeader>
              {content}
              <p className='text-sm text-muted-foreground mt-4'>
                *Dismissing this drawer, you consent to all cookies.
              </p>
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <>
          <Dialog
            open={showSettings}
            onOpenChange={async (open) => {
              if (!open) {
                await acceptAll();
              }
              setShowSettings(open);
            }}
          >
            <DialogContent className='p-0'>
              <DialogTitle className='sr-only'>Cookie Settings</DialogTitle>
              <DialogDescription className='sr-only'>
                Customize your cookie preferences
              </DialogDescription>
              <ScrollArea className='h-full max-h-[80vh]'>
                <CookieSettings
                  showTitle={true}
                  onSave={() => setShowSettings(false)}
                />
              </ScrollArea>
            </DialogContent>
          </Dialog>

          <div className='fixed bottom-0 left-0 right-0 p-4 bg-background border-t z-50'>
            <Container>{content}</Container>
          </div>
        </>
      )}
    </>
  );
}
